/**
 * Admin Dashboard - Painel Administrativo
 * Gerenciamento de empresas, planos e funcionalidades
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../lib/api';
import { ProfileRemindersCard } from '../../components/admin/ProfileRemindersCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Shield,
  Users,
  Building2,
  Briefcase,
  BarChart3,
  Settings,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  DollarSign,
  CheckCircle2,
  XCircle,
  Brain,
  UserSearch,
  ClipboardCheck,
  ArrowLeft,
  Save,
  RefreshCw,
  LogOut,
  Key,
  Mail,
  UserCog,
  Trash2,
  AlertTriangle,
  Copy,
  Clock,
  Edit,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { toast } from 'sonner';

// Feature definitions
const FEATURES = {
  selecao_aberta: {
    id: 'selecao_aberta',
    name: 'Seleção Aberta',
    description: 'Acesso à página "Selecionar Candidatos" com todos os currículos',
    icon: UserSearch
  },
  avaliacao_desempenho: {
    id: 'avaliacao_desempenho',
    name: 'Avaliação de Desempenho',
    description: 'Módulo completo de avaliação de funcionários',
    icon: ClipboardCheck
  },
  banco_questoes_disc: {
    id: 'banco_questoes_disc',
    name: 'Banco de Questões DISC',
    description: 'Acesso ao banco de perguntas comportamentais',
    icon: Brain
  },
  testes_comportamentais: {
    id: 'testes_comportamentais',
    name: 'Testes Comportamentais',
    description: 'DISC, Big Five e G2 Behavioral Profile',
    icon: Brain
  },
  pdi_pdc: {
    id: 'pdi_pdc',
    name: 'PDI / PDC',
    description: 'Plano de Desenvolvimento Individual e Coletivo',
    icon: ClipboardCheck
  },
  pesquisa_clima: {
    id: 'pesquisa_clima',
    name: 'Pesquisa de Clima',
    description: 'Pesquisas de clima organizacional',
    icon: ClipboardCheck
  },
  diagnostico_nr01: {
    id: 'diagnostico_nr01',
    name: 'Diagnóstico NR-01',
    description: 'Diagnóstico de riscos psicossociais',
    icon: ClipboardCheck
  },
  portal_denuncias: {
    id: 'portal_denuncias',
    name: 'Portal de Denúncias',
    description: 'Canal de denúncias anônimas',
    icon: ClipboardCheck
  },
  grupos_testes: {
    id: 'grupos_testes',
    name: 'Grupos de Testes',
    description: 'Aplicar testes em lote para grupos de trabalhadores',
    icon: UserSearch
  }
};

export default function AdminDashboard() {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    features: {
      selecao_aberta: true,
      avaliacao_desempenho: true,
      banco_questoes_disc: true,
      testes_comportamentais: true,
      pdi_pdc: true,
      pesquisa_clima: true,
      diagnostico_nr01: true,
      portal_denuncias: true,
      grupos_testes: true
    },
    pricing: {
      selecao_aberta: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      avaliacao_desempenho: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      banco_questoes_disc: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      testes_comportamentais: { billing_type: 'per_use', monthly_price: 0, per_use_price: 15 },
      pdi_pdc: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      pesquisa_clima: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      diagnostico_nr01: { billing_type: 'per_use', monthly_price: 0, per_use_price: 25 },
      portal_denuncias: { billing_type: 'monthly', monthly_price: 0, per_use_price: 0 },
      grupos_testes: { billing_type: 'per_use', monthly_price: 0, per_use_price: 10 },
      plano_completo: ''
    },
    plano_tipo: 'individual',
    observacoes_admin: '',
    limite_usuarios: 1
  });
  
  // User password management state
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Deletion state
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deleteCompanyDialogOpen, setDeleteCompanyDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Temp code state
  const [tempCodeDialogOpen, setTempCodeDialogOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  
  // CPF edit state
  const [cpfDialogOpen, setCpfDialogOpen] = useState(false);
  const [userToEditCpf, setUserToEditCpf] = useState(null);
  const [newCpf, setNewCpf] = useState('');
  const [savingCpf, setSavingCpf] = useState(false);
  
  // Pagination state for users table
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(20);
  
  // Platform Settings state
  const [platformSettings, setPlatformSettings] = useState(null);
  const [savingPlatformSettings, setSavingPlatformSettings] = useState(false);

  useEffect(() => {
    if (currentUser?.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    fetchData();
    fetchPlatformSettings();
  }, [currentUser, navigate]);

  const fetchPlatformSettings = async () => {
    try {
      const res = await adminAPI.getPlatformSettings();
      setPlatformSettings(res.data);
    } catch (error) {
      console.error('Error fetching platform settings:', error);
    }
  };

  const handleTogglePlatformSetting = async (key, value) => {
    try {
      setSavingPlatformSettings(true);
      const res = await adminAPI.updatePlatformSettings({ [key]: value });
      setPlatformSettings(res.data.settings);
      toast.success('Configuração atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    } finally {
      setSavingPlatformSettings(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, companiesRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getCompanies(),
        adminAPI.getUsers()
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data.companies || []);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (company) => {
    setSelectedCompany(company);
    setEditForm({
      features: company.features || {
        selecao_aberta: false,
        avaliacao_desempenho: false,
        banco_questoes_disc: false
      },
      pricing: {
        selecao_aberta: company.pricing?.selecao_aberta?.toString() || '',
        avaliacao_desempenho: company.pricing?.avaliacao_desempenho?.toString() || '',
        banco_questoes_disc: company.pricing?.banco_questoes_disc?.toString() || '',
        plano_completo: company.pricing?.plano_completo?.toString() || ''
      },
      plano_tipo: company.pricing?.plano_tipo || 'individual',
      observacoes_admin: company.observacoes_admin || '',
      limite_usuarios: company.limite_usuarios || 1
    });
    setEditDialogOpen(true);
  };

  const handleToggleApproval = async (company) => {
    const newApprovalStatus = company.aprovada === false;
    try {
      const response = await adminAPI.toggleCompanyApproval(company.id, { 
        aprovada: newApprovalStatus,
        motivo: null
      });
      toast.success(response.data.message);
      // Update local state
      setCompanies(prev => prev.map(c => {
        if (c.id === company.id) {
          return {
            ...c,
            aprovada: newApprovalStatus
          };
        }
        return c;
      }));
    } catch (error) {
      toast.error('Erro ao alterar aprovação');
    }
  };

  const handleToggleFeature = async (companyId, feature) => {
    try {
      const response = await adminAPI.toggleFeature(companyId, feature);
      toast.success(response.data.message);
      // Update local state
      setCompanies(prev => prev.map(c => {
        if (c.id === companyId) {
          return {
            ...c,
            features: {
              ...c.features,
              [feature]: response.data.enabled
            }
          };
        }
        return c;
      }));
    } catch (error) {
      toast.error('Erro ao alterar funcionalidade');
    }
  };

  const handleSavePlan = async () => {
    if (!selectedCompany) return;
    
    setSaving(true);
    try {
      // Se plano completo, habilitar todas as features automaticamente
      const features = editForm.plano_tipo === 'completo' 
        ? {
            selecao_aberta: true,
            avaliacao_desempenho: true,
            banco_questoes_disc: true
          }
        : editForm.features;
      
      // Atualizar plano (features e pricing)
      await adminAPI.updateCompanyPlan(selectedCompany.id, {
        features: features,
        pricing: {
          selecao_aberta: editForm.pricing.selecao_aberta ? parseFloat(editForm.pricing.selecao_aberta) : null,
          avaliacao_desempenho: editForm.pricing.avaliacao_desempenho ? parseFloat(editForm.pricing.avaliacao_desempenho) : null,
          banco_questoes_disc: editForm.pricing.banco_questoes_disc ? parseFloat(editForm.pricing.banco_questoes_disc) : null,
          plano_completo: editForm.pricing.plano_completo ? parseFloat(editForm.pricing.plano_completo) : null
        },
        observacoes_admin: editForm.observacoes_admin,
        limite_usuarios: parseInt(editForm.limite_usuarios) || 1
      });
      
      // Atualizar tipo de plano separadamente (via endpoint de pricing)
      await adminAPI.updateCompanyPricing(selectedCompany.id, {
        plano_tipo: editForm.plano_tipo,
        plano_completo_mensal: editForm.pricing.plano_completo ? parseFloat(editForm.pricing.plano_completo) : null
      });
      
      toast.success('Plano atualizado com sucesso!');
      setEditDialogOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowNewPassword(false);
    setForcePasswordChange(true);
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Preencha a nova senha');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setSavingPassword(true);
    try {
      await adminAPI.resetUserPassword(selectedUser.id, {
        new_password: newPassword,
        force_change: forcePasswordChange
      });
      
      toast.success(forcePasswordChange 
        ? 'Senha redefinida! O usuário precisará criar nova senha no próximo login.'
        : 'Senha redefinida com sucesso!'
      );
      setPasswordDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setSavingPassword(false);
    }
  };

  // Generate temporary code
  const handleGenerateTempCode = async (userForCode) => {
    const targetUser = userForCode || selectedUser;
    if (!targetUser) return;
    
    setSelectedUser(targetUser);
    setGeneratingCode(true);
    try {
      const response = await adminAPI.generateTempCode(targetUser.id);
      setGeneratedCode(response.data);
      setTempCodeDialogOpen(true);
      toast.success('Código temporário gerado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar código');
    } finally {
      setGeneratingCode(false);
    }
  };

  // CPF Edit functions
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2')
      .slice(0, 14);
  };

  const handleOpenCpfDialog = (userRecord) => {
    setUserToEditCpf(userRecord);
    setNewCpf(userRecord.cpf ? formatCPF(userRecord.cpf) : '');
    setCpfDialogOpen(true);
  };

  const handleSaveCpf = async () => {
    if (!userToEditCpf) return;
    
    const cleanCpf = newCpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) {
      toast.error('CPF deve ter 11 dígitos');
      return;
    }
    
    setSavingCpf(true);
    try {
      await adminAPI.updateUserCpf(userToEditCpf.id, cleanCpf);
      toast.success(`CPF do usuário ${userToEditCpf.email} atualizado!`);
      setCpfDialogOpen(false);
      setUserToEditCpf(null);
      setNewCpf('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar CPF');
    } finally {
      setSavingCpf(false);
    }
  };

  // Delete user
  const handleOpenDeleteUserDialog = (user) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await adminAPI.deleteUser(userToDelete.id);
      toast.success(`Usuário ${userToDelete.email} excluído com sucesso!`);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeleting(false);
    }
  };

  // Delete company
  const handleOpenDeleteCompanyDialog = (company) => {
    setCompanyToDelete(company);
    setDeleteCompanyDialogOpen(true);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    setDeleting(true);
    try {
      const response = await adminAPI.deleteCompany(companyToDelete.id);
      toast.success(`Empresa ${companyToDelete.nome} excluída! (${response.data.deleted_jobs} vagas, ${response.data.deleted_employees} funcionários removidos)`);
      setDeleteCompanyDialogOpen(false);
      setCompanyToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir empresa');
    } finally {
      setDeleting(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const filteredUsers = allUsers.filter(u =>
    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.cpf?.includes(userSearchTerm) ||
    u.nome?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );
  
  // Pagination calculations for users
  const totalUsers = filteredUsers.length;
  const totalUsersPages = Math.ceil(totalUsers / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * usersPerPage,
    usersPage * usersPerPage
  );
  
  // Reset to page 1 when search changes
  useEffect(() => {
    setUsersPage(1);
  }, [userSearchTerm]);

  const filteredCompanies = companies.filter(c => 
    c.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cnpj?.includes(searchTerm) ||
    c.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const countEnabledFeatures = (company) => {
    const features = company.features || {};
    return Object.values(features).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-900">Painel Administrativo</h1>
                  <p className="text-xs text-slate-500">Gerenciamento de Planos e Acessos</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/admin/financeiro')}
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Financeiro
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { logout(); navigate('/login'); }}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats?.users || 0}</p>
              <p className="text-sm text-slate-500">Usuários</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats?.companies || 0}</p>
              <p className="text-sm text-slate-500">Empresas</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <UserSearch className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats?.candidates || 0}</p>
              <p className="text-sm text-slate-500">Candidatos</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Briefcase className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats?.jobs || 0}</p>
              <p className="text-sm text-slate-500">Vagas</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats?.applications || 0}</p>
              <p className="text-sm text-slate-500">Candidaturas</p>
            </CardContent>
          </Card>
        </div>

        {/* Admin Credentials Card */}
        <Card className="border-red-200 bg-red-50/50 mb-8">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-700 text-lg">
              <Shield className="w-5 h-5" />
              Credenciais do Administrador
            </CardTitle>
            <CardDescription className="text-red-600">
              Informações de acesso ao painel administrativo
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-slate-600">Usuário</span>
                </div>
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">admin@g2gente.com</code>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard('admin@g2gente.com')}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-slate-600">Senha</span>
                </div>
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">admin123</code>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard('admin123')}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-red-500 mt-3 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Em produção, altere a senha padrão imediatamente por segurança.
            </p>
          </CardContent>
        </Card>

        {/* Platform Settings */}
        <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-cyan-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-900">
              <Settings className="w-5 h-5" />
              Configurações da Plataforma
            </CardTitle>
            <CardDescription className="text-cyan-700">
              Configurações globais que afetam todos os usuários
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Employee Job Search Toggle */}
            <div className="bg-white rounded-lg p-4 border border-cyan-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-900">Busca de Vagas para Funcionários</h4>
                  <p className="text-sm text-slate-500">
                    Quando desabilitado, funcionários ativos não terão acesso a: Explorar Vagas, Minhas Candidaturas, Solicitações de Contato e Mensagens
                  </p>
                </div>
                <Switch
                  checked={platformSettings?.employee_job_search_enabled || false}
                  onCheckedChange={(checked) => handleTogglePlatformSetting('employee_job_search_enabled', checked)}
                  disabled={savingPlatformSettings}
                />
              </div>
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-slate-500">
                  Status atual: {platformSettings?.employee_job_search_enabled ? (
                    <Badge className="bg-green-100 text-green-700 ml-2">Habilitado</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 ml-2">Desabilitado</Badge>
                  )}
                </p>
              </div>
            </div>

            {/* Features Summary */}
            {platformSettings?.employee_features && (
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-slate-900 mb-3">Features de Funcionários</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(platformSettings.employee_features).map(([key, enabled]) => (
                    <div key={key} className="flex items-center gap-2">
                      {enabled ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300" />
                      )}
                      <span className={`text-sm ${enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Companies Management */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cyan-600" />
                  Empresas Cadastradas
                </CardTitle>
                <CardDescription>
                  Gerencie planos e funcionalidades de cada empresa
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar empresa..."
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Email/CPF</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Usuários</TableHead>
                  <TableHead className="text-center">Seleção Aberta</TableHead>
                  <TableHead className="text-center">Avaliação</TableHead>
                  <TableHead className="text-center">DISC</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Nenhuma empresa encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompanies.map((company) => (
                    <TableRow key={company.id} className={!company.aprovada ? 'bg-amber-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{company.nome}</p>
                            <p className="text-xs text-slate-500">
                              {company.cnpj ? `CNPJ: ${company.cnpj}` : 'Pessoa Física'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600">{company.user_email}</p>
                        {company.user_cpf && (
                          <p className="text-xs text-slate-400">CPF: {company.user_cpf}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleApproval(company)}
                          className={company.aprovada !== false ? 'text-green-600' : 'text-amber-500'}
                          data-testid={`approval-btn-${company.id}`}
                        >
                          {company.aprovada !== false ? (
                            <div className="flex items-center gap-1">
                              <CheckCircle2 className="w-5 h-5" />
                              <span className="text-xs">Aprovada</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Clock className="w-5 h-5" />
                              <span className="text-xs">Pendente</span>
                            </div>
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={company.usuarios_count >= company.limite_usuarios ? "destructive" : "secondary"}>
                          {company.usuarios_count || 1}/{company.limite_usuarios || 1}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeature(company.id, 'selecao_aberta')}
                          className={company.features?.selecao_aberta ? 'text-green-600' : 'text-red-500'}
                        >
                          {company.features?.selecao_aberta ? (
                            <Unlock className="w-5 h-5" />
                          ) : (
                            <Lock className="w-5 h-5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeature(company.id, 'avaliacao_desempenho')}
                          className={company.features?.avaliacao_desempenho ? 'text-green-600' : 'text-red-500'}
                        >
                          {company.features?.avaliacao_desempenho ? (
                            <Unlock className="w-5 h-5" />
                          ) : (
                            <Lock className="w-5 h-5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeature(company.id, 'banco_questoes_disc')}
                          className={company.features?.banco_questoes_disc ? 'text-green-600' : 'text-red-500'}
                        >
                          {company.features?.banco_questoes_disc ? (
                            <Unlock className="w-5 h-5" />
                          ) : (
                            <Lock className="w-5 h-5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenEditDialog(company)}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDeleteCompanyDialog(company)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-6 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Unlock className="w-4 h-4 text-green-600" />
            <span>Liberado</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-red-500" />
            <span>Bloqueado</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Clique nos ícones para liberar/bloquear rapidamente</span>
          </div>
        </div>

        <ProfileRemindersCard />

        {/* Users Password Management */}
        <Card className="border-slate-200 mt-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-cyan-600" />
                  Gerenciamento de Senhas
                </CardTitle>
                <CardDescription>
                  Redefina senhas e force alteração no próximo login
                </CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Exibir:</span>
                  <Select value={usersPerPage.toString()} onValueChange={(v) => { setUsersPerPage(Number(v)); setUsersPage(1); }}>
                    <SelectTrigger className="w-[80px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Buscar por email, CPF ou nome..."
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            {/* Total count */}
            <div className="mt-2 text-sm text-slate-500">
              {totalUsers === allUsers.length 
                ? `Total: ${totalUsers} usuário(s)` 
                : `Exibindo ${totalUsers} de ${allUsers.length} usuário(s)`}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-slate-500" />
                          </div>
                          <p className="font-medium text-slate-900">{user.nome || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600">{user.email}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500">
                          {user.cpf ? user.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          user.role === 'ADMIN' ? 'destructive' :
                          user.role === 'COMPANY' ? 'default' :
                          user.role === 'PARTNER' ? 'secondary' : 'outline'
                        }>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {user.force_password_change ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                            <Key className="w-3 h-3 mr-1" />
                            Deve trocar senha
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Ativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenCpfDialog(user)}
                            title="Editar CPF"
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPasswordDialog(user)}
                            title="Redefinir senha"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGenerateTempCode(user)}
                            title="Gerar código temporário"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          {/* Allow deleting any user except yourself */}
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDeleteUserDialog(user)}
                              title="Excluir usuário"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            {/* Pagination Controls */}
            {totalUsersPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-slate-500">
                  Página {usersPage} de {totalUsersPages} ({totalUsers} usuário{totalUsers !== 1 ? 's' : ''})
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(1)}
                    disabled={usersPage === 1}
                    title="Primeira página"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                    disabled={usersPage === 1}
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex items-center gap-1 mx-2">
                    {Array.from({ length: Math.min(5, totalUsersPages) }, (_, i) => {
                      let pageNum;
                      if (totalUsersPages <= 5) {
                        pageNum = i + 1;
                      } else if (usersPage <= 3) {
                        pageNum = i + 1;
                      } else if (usersPage >= totalUsersPages - 2) {
                        pageNum = totalUsersPages - 4 + i;
                      } else {
                        pageNum = usersPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={usersPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setUsersPage(pageNum)}
                          className={usersPage === pageNum ? "bg-cyan-600 hover:bg-cyan-700" : ""}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => Math.min(totalUsersPages, p + 1))}
                    disabled={usersPage === totalUsersPages}
                    title="Próxima página"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(totalUsersPages)}
                    disabled={usersPage === totalUsersPages}
                    title="Última página"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-600" />
              Configurar Plano - {selectedCompany?.nome_fantasia || selectedCompany?.razao_social || selectedCompany?.nome}
            </DialogTitle>
            <DialogDescription>
              Configure funcionalidades, tipo de cobrança e valores
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="features" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="features">Funcionalidades e Valores</TabsTrigger>
              <TabsTrigger value="plano">Tipo de Plano</TabsTrigger>
              <TabsTrigger value="users">Usuários</TabsTrigger>
            </TabsList>

            <TabsContent value="features" className="space-y-4 mt-4">
              <p className="text-sm text-slate-500">
                Ative as funcionalidades e defina o tipo de cobrança e valores
              </p>
              
              {/* Aviso para Plano Completo */}
              {editForm.plano_tipo === 'completo' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Plano Completo Ativo</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Todas as funcionalidades estão automaticamente habilitadas neste plano.
                  </p>
                </div>
              )}
              
              {/* Grid de Features - 2 colunas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(FEATURES).map(([key, feature]) => {
                  const Icon = feature.icon;
                  const isPlanoCompleto = editForm.plano_tipo === 'completo';
                  const isEnabled = isPlanoCompleto || editForm.features[key] || false;
                  const pricing = editForm.pricing[key] || {};
                  
                  return (
                    <div 
                      key={key} 
                      className={`p-3 rounded-lg border transition-colors ${
                        isEnabled ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                      } ${isPlanoCompleto ? 'opacity-75' : ''}`}
                    >
                      {/* Header: Nome + Toggle */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                          <span className="font-medium text-sm">{feature.name}</span>
                        </div>
                        <Switch
                          checked={isEnabled}
                          disabled={isPlanoCompleto}
                          onCheckedChange={(checked) => setEditForm(prev => ({
                            ...prev,
                            features: { ...prev.features, [key]: checked }
                          }))}
                        />
                      </div>
                      
                      {/* Campos de Valor - só mostra se habilitado */}
                      {isEnabled && (
                        <div className="space-y-2 pt-2 border-t border-slate-200">
                          {/* Tipo de Cobrança */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500 w-16">Cobrança:</Label>
                            <select
                              value={pricing.billing_type || 'monthly'}
                              onChange={(e) => setEditForm(prev => ({
                                ...prev,
                                pricing: {
                                  ...prev.pricing,
                                  [key]: { ...prev.pricing[key], billing_type: e.target.value }
                                }
                              }))}
                              className="flex-1 h-8 text-xs border rounded px-2"
                            >
                              <option value="monthly">Mensal</option>
                              <option value="per_use">Por Uso</option>
                              <option value="included">Incluído no Plano</option>
                            </select>
                          </div>
                          
                          {/* Valores */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-500">R$ Mensal</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricing.monthly_price || ''}
                                onChange={(e) => setEditForm(prev => ({
                                  ...prev,
                                  pricing: {
                                    ...prev.pricing,
                                    [key]: { ...prev.pricing[key], monthly_price: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                                placeholder="0,00"
                                className="h-8 text-xs"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">R$ Por Uso</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pricing.per_use_price || ''}
                                onChange={(e) => setEditForm(prev => ({
                                  ...prev,
                                  pricing: {
                                    ...prev.pricing,
                                    [key]: { ...prev.pricing[key], per_use_price: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                                placeholder="0,00"
                                className="h-8 text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="plano" className="space-y-4 mt-4">
              <p className="text-sm text-slate-500 mb-4">
                Defina o tipo de plano da empresa
              </p>
              
              {/* Tipo de Plano */}
              <div className="space-y-3">
                <div 
                  onClick={() => setEditForm(prev => ({ ...prev, plano_tipo: 'individual' }))}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    editForm.plano_tipo === 'individual' 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      editForm.plano_tipo === 'individual' ? 'border-cyan-500 bg-cyan-500' : 'border-slate-300'
                    }`}>
                      {editForm.plano_tipo === 'individual' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Plano Individual</p>
                      <p className="text-sm text-slate-500">Cobra por cada funcionalidade habilitada separadamente</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  onClick={() => setEditForm(prev => ({ ...prev, plano_tipo: 'completo' }))}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    editForm.plano_tipo === 'completo' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      editForm.plano_tipo === 'completo' ? 'border-green-500 bg-green-500' : 'border-slate-300'
                    }`}>
                      {editForm.plano_tipo === 'completo' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Plano Completo</p>
                      <p className="text-sm text-slate-500">Valor mensal fixo com acesso a todas as funcionalidades</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Valor do Plano Completo */}
              {editForm.plano_tipo === 'completo' && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-sm font-medium text-green-800">Valor Mensal do Plano Completo</Label>
                  <div className="relative mt-2">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-600 w-4 h-4" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.pricing.plano_completo || ''}
                      onChange={(e) => setEditForm(prev => ({
                        ...prev,
                        pricing: { ...prev.pricing, plano_completo: e.target.value }
                      }))}
                      placeholder="499,90"
                      className="pl-10 border-green-300 focus:border-green-500"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="users" className="space-y-4 mt-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
                    <Users className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Limite de Usuários</p>
                    <p className="text-sm text-slate-500">Quantidade máxima de usuários para esta empresa</p>
                  </div>
                </div>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={editForm.limite_usuarios}
                  onChange={(e) => setEditForm(prev => ({
                    ...prev,
                    limite_usuarios: e.target.value
                  }))}
                  className="w-24 text-center"
                />
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Uso atual:</strong> {selectedCompany?.usuarios_count || 1} de {selectedCompany?.limite_usuarios || 1} usuários
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-4">
            <Label>Observações do Admin</Label>
            <Textarea
              value={editForm.observacoes_admin}
              onChange={(e) => setEditForm(prev => ({ ...prev, observacoes_admin: e.target.value }))}
              placeholder="Anotações internas sobre esta empresa..."
              rows={3}
              className="mt-2"
            />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSavePlan}
              disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-600" />
              Redefinir Senha
            </DialogTitle>
            <DialogDescription>
              Defina uma nova senha para o usuário {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm"><strong>Usuário:</strong> {selectedUser?.nome || selectedUser?.email}</p>
              <p className="text-sm text-slate-500"><strong>Email:</strong> {selectedUser?.email}</p>
              {selectedUser?.cpf && (
                <p className="text-sm text-slate-500"><strong>CPF:</strong> {selectedUser.cpf}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Checkbox
                id="force-change"
                checked={forcePasswordChange}
                onCheckedChange={setForcePasswordChange}
              />
              <div>
                <Label htmlFor="force-change" className="text-sm font-medium text-amber-800 cursor-pointer">
                  Forçar mudança no próximo login
                </Label>
                <p className="text-xs text-amber-600">
                  O usuário será obrigado a criar uma nova senha ao fazer login
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={savingPassword || !newPassword}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {savingPassword ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Redefinir Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Exclusão de Usuário
            </DialogTitle>
            <DialogDescription>
              Esta ação é irreversível. Todos os dados associados serão excluídos.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-800">Usuário a ser excluído:</p>
              <p className="text-sm text-red-700 mt-1">{userToDelete?.email}</p>
              <p className="text-sm text-red-600">{userToDelete?.nome || 'Sem nome'}</p>
              <Badge className="mt-2" variant="outline">{userToDelete?.role}</Badge>
            </div>
            
            {userToDelete?.role === 'COMPANY' && (
              <p className="text-xs text-red-600 mt-3">
                ⚠️ A empresa e todos os dados relacionados (vagas, funcionários) também serão excluídos.
              </p>
            )}
            {userToDelete?.role === 'CANDIDATE' && (
              <p className="text-xs text-red-600 mt-3">
                ⚠️ O currículo e todas as candidaturas serão excluídos.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteUser}
              disabled={deleting}
              variant="destructive"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company Confirmation Dialog */}
      <Dialog open={deleteCompanyDialogOpen} onOpenChange={setDeleteCompanyDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Exclusão de Empresa
            </DialogTitle>
            <DialogDescription>
              Esta ação é irreversível. Todos os dados da empresa serão excluídos.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-800">Empresa a ser excluída:</p>
              <p className="text-sm text-red-700 mt-1 font-semibold">{companyToDelete?.nome}</p>
              {companyToDelete?.cnpj && (
                <p className="text-sm text-red-600">CNPJ: {companyToDelete.cnpj}</p>
              )}
              <p className="text-sm text-red-600">Email: {companyToDelete?.user_email}</p>
            </div>
            
            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 font-medium">Serão excluídos:</p>
              <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">
                <li>Todas as vagas publicadas</li>
                <li>Todos os funcionários cadastrados</li>
                <li>PDIs, PDCs e avaliações</li>
                <li>Ciclos de desempenho</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCompanyDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteCompany}
              disabled={deleting}
              variant="destructive"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Code Dialog */}
      <Dialog open={tempCodeDialogOpen} onOpenChange={setTempCodeDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Clock className="w-5 h-5" />
              Código Temporário Gerado
            </DialogTitle>
            <DialogDescription>
              Envie este código para o usuário. Ele expira em 24 horas.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 mb-2">
                <strong>Usuário:</strong> {generatedCode?.user_email}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-white rounded-lg border-2 border-blue-300 text-center">
                  <p className="text-3xl font-mono font-bold text-blue-800 tracking-widest">
                    {generatedCode?.code}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(generatedCode?.code)}
                  className="text-blue-600"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                Expira em: {new Date(generatedCode?.expires_at).toLocaleString('pt-BR')}
              </p>
            </div>

            <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
              <p className="text-sm font-medium text-slate-700">Instruções para o usuário:</p>
              <ol className="text-xs text-slate-600 mt-2 list-decimal list-inside space-y-1">
                <li>Acesse a página de login</li>
                <li>Clique em &quot;Esqueci minha senha&quot;</li>
                <li>Escolha &quot;Tenho um código&quot;</li>
                <li>Digite o email e o código acima</li>
                <li>Crie uma nova senha</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTempCodeDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CPF Edit Dialog */}
      <Dialog open={cpfDialogOpen} onOpenChange={setCpfDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Editar CPF do Usuário</DialogTitle>
            <DialogDescription>
              {userToEditCpf && (
                <span>
                  Editando CPF de: <strong>{userToEditCpf.email}</strong>
                  <br />
                  CPF atual: <strong>{userToEditCpf.cpf || 'Não cadastrado'}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="newCpf">Novo CPF</Label>
            <Input
              id="newCpf"
              value={newCpf}
              onChange={(e) => setNewCpf(formatCPF(e.target.value))}
              placeholder="000.000.000-00"
              maxLength={14}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCpfDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCpf} disabled={savingCpf}>
              {savingCpf ? 'Salvando...' : 'Salvar CPF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
